/**
 * Dadware UGC videos — dw-ugc.js (deferred).
 *
 * Playback with nothing to press: every clip plays on its own while it is in
 * view, on desktop and phones alike, muted and looped. Hovering is not needed.
 * Tap still toggles play/pause, and sound only after the visitor unmutes.
 * Clips out of view are paused to save battery and bandwidth, and a clip the
 * visitor paused by hand stays paused. Skipped entirely for reduced motion
 * and data saver.
 */
(() => {
  'use strict';
  if (customElements.get('dw-ugc')) return;

  class DwUgc extends HTMLElement {
    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('.dw-ugc__card')).filter((card) => card.querySelector('video'));
      if (!this.cards.length) return;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const saveData = Boolean(navigator.connection && navigator.connection.saveData);
      this.previews = this.dataset.autopreview === 'true' && !reduced && !saveData;

      this.cards.forEach((card) => {
        const video = card.querySelector('video');
        video.muted = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.addEventListener('play', () => this.setState(card, true));
        video.addEventListener('pause', () => this.setState(card, false));
      });

      this.onClick = (event) => {
        const play = event.target.closest('[data-dw-ugc-play]');
        if (play) {
          this.toggle(play.closest('.dw-ugc__card'));
          return;
        }
        const sound = event.target.closest('[data-dw-ugc-sound]');
        if (sound) this.toggleSound(sound.closest('.dw-ugc__card'));
      };
      this.addEventListener('click', this.onClick);

      // Every clip plays itself while on screen — same behaviour on desktop and
      // phones. Nothing is gated behind hover, and starting one no longer stops
      // the others: the rail is meant to be alive all at once.
      if (this.previews && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const card = entry.target;
              const video = card.querySelector('video');
              if (!video || card.dataset.userPaused) return;
              // A third visible is enough: in a horizontal rail the edge cards
              // are only ever partly on screen.
              if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
                if (video.preload === 'none') video.preload = 'metadata';
                video.play().catch(() => {});
              } else if (!video.paused && !card.dataset.userPlayed) {
                // Off screen: stop, to spare battery and data.
                video.pause();
              }
            });
          },
          { threshold: [0, 0.35, 0.6] }
        );
        this.cards.forEach((card) => this.observer.observe(card));
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onClick) this.removeEventListener('click', this.onClick);
    }

    toggle(card) {
      const video = card && card.querySelector('video');
      if (!video) return;
      if (video.paused) {
        card.dataset.userPlayed = 'true';
        delete card.dataset.userPaused;
        video.play().catch(() => {});
      } else {
        card.dataset.userPaused = 'true';
        video.pause();
      }
    }

    toggleSound(card) {
      const video = card && card.querySelector('video');
      const button = card && card.querySelector('[data-dw-ugc-sound]');
      if (!video || !button) return;
      video.muted = !video.muted;
      button.setAttribute('aria-pressed', String(!video.muted));
      button.setAttribute('aria-label', video.muted ? button.dataset.labelUnmute : button.dataset.labelMute);
      button.querySelector('[data-icon-off]').hidden = !video.muted;
      button.querySelector('[data-icon-on]').hidden = video.muted;
    }

    setState(card, playing) {
      card.classList.toggle('is-playing', playing);
      const play = card.querySelector('[data-dw-ugc-play]');
      if (play) {
        play.setAttribute('aria-pressed', String(playing));
        play.setAttribute('aria-label', playing ? play.dataset.labelPause : play.dataset.labelPlay);
      }
      const sound = card.querySelector('[data-dw-ugc-sound]');
      if (sound) sound.hidden = !playing && card.querySelector('video').muted;
    }
  }

  customElements.define('dw-ugc', DwUgc);
})();
